import { logger } from '@multiplatform-apps/service-trips';
import React, { Component, ErrorInfo, ReactNode, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useDeviceType } from '../../hooks';
import { ErrorView } from '../error-view';
import { ErrorBoundaryProps, ErrorBoundaryState, ErrorFallbackProps } from './types';

/** Default fallback. Needs router context — use RootErrorFallback above <RouterProvider>. */
const ErrorFallback = ({ error, onReset }: ErrorFallbackProps): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const deviceType = useDeviceType();
  const containerRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef(location.pathname);

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (location.pathname !== locationRef.current) {
      locationRef.current = location.pathname;
      onReset();
    }
  }, [location.pathname, onReset]);

  return (
    <ErrorView
      error={error}
      isMobile={deviceType === 'mobile'}
      onReturnHome={() => navigate('/')}
      onReload={() => window.location.reload()}
      containerRef={containerRef}
    />
  );
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error('ErrorBoundary caught', error, { componentStack: errorInfo.componentStack });
  }

  reset(): void {
    this.setState({ hasError: false, error: null });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallback } = this.props;
      if (fallback) {
        return typeof fallback === 'function' ? fallback(this.state.error) : fallback;
      }
      return <ErrorFallback error={this.state.error} onReset={this.reset} />;
    }
    return this.props.children;
  }
}
